import { ChevronDown, Building2 } from 'lucide-react';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { cn } from '@/lib/utils';
import { useState, useRef, useEffect } from 'react';

export function WorkspaceSelector() {
  const { workspaces, currentWorkspace, setCurrentWorkspace, isLoading } = useCurrentWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="h-8 w-40 bg-gray-100 animate-pulse rounded-brand" />
    );
  }

  if (workspaces.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Sélectionner un workspace"
        className="flex items-center gap-2 px-3 py-1.5 rounded-brand border border-brand-border bg-white hover:bg-gray-50 transition-colors text-sm font-medium text-brand-dark"
      >
        <Building2 size={14} className="text-brand-primary shrink-0" />
        <span className="max-w-[140px] truncate">{currentWorkspace?.name ?? '—'}</span>
        <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-brand-border rounded-brand shadow-lg py-1">
          {workspaces.map((ws) => (
            <button
              key={ws.id}
              type="button"
              onClick={() => {
                setCurrentWorkspace(ws.id);
                setOpen(false);
              }}
              className={cn(
                'w-full text-left px-4 py-2 text-sm transition-colors',
                ws.id === currentWorkspace?.id
                  ? 'text-brand-primary font-medium bg-brand-primary-light'
                  : 'text-gray-700 hover:bg-gray-50',
              )}
            >
              {ws.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
